/**
 * What was actually sent to the model.
 *
 * ## Why this exists
 *
 * Two questions had no answer from inside the app. "Is the look-ahead even working?" —
 * which is a question about the server's cache, invisible from a graph. And "did it hear
 * what I asked for?" — which is a question about the prompt, previously answerable only by
 * reading the server's source. Both are now one tap: every answered request lands in the
 * ring with its prompts, its model, its timing, and whether it came from the cache.
 *
 * `cached` is the loudest thing on each card on purpose. It is the evidence for the
 * look-ahead: a speculative request generating an answer, and then the real request for
 * the same chord returning it in single-digit milliseconds, is what "it works" looks like
 * — and until now nothing on screen could distinguish that from a fast day.
 *
 * ## Costing nothing when it is off
 *
 * Nothing here is mounted unless the setting is on, the ring it reads is empty until
 * something records into it, and nothing records unless a request asked for a prompt.
 */

import { useCallback, useState, useSyncExternalStore } from 'react';
import { BottomSheet } from '../system/BottomSheet.js';
import {
  clearDebugLog,
  getDebugEntries,
  subscribeToDebugLog,
  type DebugEntry,
  type DebugRoute,
} from '../../state/debugLog.js';
import './debugsheet.css';

export interface DebugSheetProps {
  open: boolean;
  onClose: () => void;
  /** Overrides the ring. Tests only — the live sheet reads the module. */
  entries?: readonly DebugEntry[];
  /** Injected so the copy button is assertable without a clipboard permission. */
  copyText?: (text: string) => void;
}

/** What each route is called in front of a person. */
const ROUTE_LABELS: Readonly<Record<DebugRoute, string>> = Object.freeze({
  branches: 'branches',
  'branches:look-ahead': 'branches · look-ahead',
  command: 'command',
  explain: 'explain',
});

function defaultCopy(text: string): void {
  // `navigator.clipboard` is absent over plain HTTP and in older WebViews. A copy button
  // that does nothing is a smaller problem than a sheet that throws.
  void navigator?.clipboard?.writeText(text).catch(() => {});
}

export function DebugSheet({
  open,
  onClose,
  entries: injected,
  copyText = defaultCopy,
}: DebugSheetProps): React.JSX.Element {
  const live = useSyncExternalStore(subscribeToDebugLog, getDebugEntries, getDebugEntries);
  const entries = injected ?? live;

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title="Model prompts"
      eyebrow="what was sent"
      testId="hc-debug-sheet"
    >
      {entries.length === 0 ? (
        <p className="hc-settings__hint" data-testid="hc-debug-empty">
          Nothing yet. Play a chord and the request that answered it will appear here.
        </p>
      ) : (
        <>
          <ol className="hc-debug__list">
            {entries.map((entry) => (
              <DebugCard key={entry.id} entry={entry} copyText={copyText} />
            ))}
          </ol>
          <button
            type="button"
            className="hc-button hc-button--quiet"
            data-testid="hc-debug-clear"
            onClick={() => clearDebugLog()}
          >
            Clear
          </button>
        </>
      )}
    </BottomSheet>
  );
}

function DebugCard({
  entry,
  copyText,
}: {
  entry: DebugEntry;
  copyText: (text: string) => void;
}): React.JSX.Element {
  const [copied, setCopied] = useState(false);

  const copy = useCallback(() => {
    copyText(`SYSTEM\n${entry.system}\n\nUSER\n${entry.user}`);
    setCopied(true);
  }, [copyText, entry.system, entry.user]);

  return (
    <li className="hc-debug__entry" data-testid="hc-debug-entry" data-route={entry.route}>
      <header className="hc-debug__head">
        <span className="hc-debug__route">{ROUTE_LABELS[entry.route]}</span>
        {/*
          The one fact this sheet was built to show, and the only thing on the card that
          gets a colour. A cache hit is the look-ahead having worked.
        */}
        <span
          className={`hc-debug__cached${entry.cached ? ' is-cached' : ''}`}
          data-testid="hc-debug-cached"
        >
          {entry.cached ? 'CACHED' : 'GENERATED'}
        </span>
      </header>

      <dl className="hc-debug__facts">
        <div>
          <dt>model</dt>
          <dd data-testid="hc-debug-model">{entry.model}</dd>
        </div>
        <div>
          <dt>took</dt>
          <dd data-testid="hc-debug-ms">{Math.round(entry.ms)} ms</dd>
        </div>
        <div>
          <dt>effort</dt>
          <dd data-testid="hc-debug-effort">{entry.effort ?? '—'}</dd>
        </div>
        <div>
          <dt>palette</dt>
          <dd data-testid="hc-debug-palette">
            {entry.paletteKinds.length > 0 ? entry.paletteKinds.join(', ') : '—'}
          </dd>
        </div>
      </dl>

      <button
        type="button"
        className="hc-button hc-button--quiet hc-debug__copy"
        data-testid="hc-debug-copy"
        onClick={copy}
      >
        {copied ? 'Copied' : 'Copy prompt'}
      </button>

      <h4 className="hc-debug__label">system</h4>
      <pre className="hc-debug__prompt" data-testid="hc-debug-system">
        {entry.system}
      </pre>
      <h4 className="hc-debug__label">user</h4>
      <pre className="hc-debug__prompt" data-testid="hc-debug-user">
        {entry.user}
      </pre>
    </li>
  );
}

export default DebugSheet;
